import { getLogger } from '@/lib/logger'

export function parseTranscript(transcript) {
  if (!transcript) return []

  const lines = transcript.split('\n')
  const messages = []

  for (const line of lines) {
    const trimmedLine = line.trim()

    if (!trimmedLine ||
        trimmedLine.startsWith('[') ||
        trimmedLine.startsWith('{') ||
        trimmedLine.startsWith('<function:')) {
      continue
    }

    if (trimmedLine.startsWith('aimee:')) {
      messages.push({
        text: trimmedLine.substring(6).trim(),
        isAimee: true
      })
    } else if (trimmedLine.startsWith('patient/speaker:')) {
      messages.push({
        text: trimmedLine.substring(16).trim(),
        isAimee: false
      })
    } else if (trimmedLine.startsWith('user:')) {
      messages.push({
        text: trimmedLine.substring(5).trim(),
        isAimee: false
      })
    }
  }

  return messages
}

export function getMessageCount(transcript) {
  const messages = parseTranscript(transcript)
  return messages.length
}

export function getConversationPreview(transcript, maxMessages = 2, maxLength = 40) {
  const messages = parseTranscript(transcript)

  if (messages.length === 0) return ['No valid messages']

  return messages.slice(0, maxMessages).map(message => {
    const speaker = message.isAimee ? 'Aimee' : 'Patient'
    const text = message.text.length > maxLength
      ? message.text.substring(0, maxLength) + '...'
      : message.text
    return `${speaker}: ${text}`
  })
}

const logger = getLogger('aimee:camera')

async function captureCameraFrame(videoTrack, maxWidth = 1024) {
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.srcObject = new MediaStream([videoTrack])
  await video.play()

  const scale = Math.min(1, maxWidth / video.videoWidth)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(video.videoWidth * scale)
  canvas.height = Math.round(video.videoHeight * scale)
  canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
  video.srcObject = null

  const imageUrl = canvas.toDataURL('image/jpeg', 0.7)
  logger.info('frame captured', { width: canvas.width, height: canvas.height, size_kb: Math.round(imageUrl.length / 1024) })
  return imageUrl
}

// Logs OpenAI's ack (or error) for the image item so we can tell if the image actually landed.
function logImageAck(dc, itemId) {
  const done = () => {
    clearTimeout(timeoutId)
    dc.removeEventListener('message', onMessage)
  }
  const onMessage = (e) => {
    const msg = JSON.parse(e.data)
    if (msg.type === 'error' && msg.error?.event_id === itemId) {
      logger.error('openai rejected image', { item_id: itemId }, msg.error)
      done()
    } else if (msg.item?.id === itemId) {
      logger.info('openai received image', { item_id: itemId, event_type: msg.type })
      done()
    }
  }
  const timeoutId = setTimeout(() => {
    logger.warn('no openai image confirmation after 10s', { item_id: itemId })
    dc.removeEventListener('message', onMessage)
  }, 10000)
  dc.addEventListener('message', onMessage)
}

// Answers a look_at_camera tool call. Realtime function_call_output only accepts text,
// so the frame is sent as a separate user message with an input_image part.
// The frame is kept small because it travels over the WebRTC data channel (~256KB message limit).
export async function sendCameraFrame(dc, callId, mediaStream) {
  logger.info('look_at_camera called', { call_id: callId })
  const videoTrack = mediaStream?.getVideoTracks()[0]
  let imageUrl = null
  let output = { success: true }

  if (!videoTrack || videoTrack.readyState !== 'live') {
    logger.warn('no live camera track, reporting camera unavailable', { call_id: callId })
    output = { success: false, error: 'Camera is not available' }
  } else {
    try {
      imageUrl = await captureCameraFrame(videoTrack)
    } catch (e) {
      logger.error('failed to capture camera frame', { call_id: callId }, e)
      output = { success: false, error: 'Failed to capture camera frame' }
    }
  }

  dc.send(JSON.stringify({
    type: 'conversation.item.create',
    item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(output) }
  }))

  if (imageUrl) {
    const itemId = `img_${Date.now()}`
    logImageAck(dc, itemId)
    try {
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        event_id: itemId,
        item: {
          id: itemId,
          type: 'message',
          role: 'user',
          content: [{ type: 'input_image', image_url: imageUrl }]
        }
      }))
      logger.info('image sent', { item_id: itemId })
    } catch (e) {
      // dc.send throws if the message exceeds the data channel's max message size
      logger.error('failed to send image over data channel', { item_id: itemId }, e)
    }
  }

  dc.send(JSON.stringify({ type: 'response.create' }))
}

export function downloadConversationTranscript(conversation, formatDate) {
  if (!conversation?.transcript) return false

  const header = `Aimee Conversation Transcript
Date: ${formatDate(conversation.created_at)}
Patient ID: ${conversation.patient_monitor.patient.id}
Room: ${conversation.patient_monitor.room.name}
Hospital: ${conversation.patient_monitor.division.name}
Prompt Name: ${conversation.aimee_prompt?.name || 'N/A'}
Source: ${conversation.source}
${conversation.ended_at ? `Ended: ${formatDate(conversation.ended_at)}` : 'Status: Active'}

--- Raw Conversation ---

`

  const fullContent = header + conversation.transcript

  const blob = new Blob([fullContent], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const date = formatDate(conversation.created_at, 'YYYY-MM-DD')
  const patientId = `patient-${conversation.patient_monitor.patient.id}`
  const room = `room-${conversation.patient_monitor.room.name}`.replace(/[^a-zA-Z0-9]+/g, '-')

  link.download = `aimee_conversation_${date}_${patientId}_${room}.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)

  return true
} 